import React, { useEffect, useMemo, useState } from "react";
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Modal,
  Select,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import {
  IconEdit,
  IconPlus,
  IconSearch,
  IconTrash,
  IconX,
} from "@tabler/icons-react";
import { showNotification } from "@mantine/notifications";
import { BeatLoader } from "react-spinners";
import DeleteConfirmationModal from "../../../components/DeleteConfirmationModal";
import { apiWrapWithErrorWithData } from "../../../utilities/apiHelpers";
import {
  createAgreementCategory,
  deleteAgreementCategory,
  getAgreementCategories,
  updateAgreementCategory,
} from "../../../utilities/apis/agreementCategories";
import colors from "../../../utilities/design";

const normalizeActive = (isActive) => {
  if (isActive === true || isActive === 1 || isActive === "1") return true;
  if (isActive === false || isActive === 0 || isActive === "0") return false;
  return Boolean(isActive);
};

const AgreementCategory = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formName, setFormName] = useState("");
  const [formIsActive, setFormIsActive] = useState("true");
  const [formError, setFormError] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);

  const fetchCategories = async () => {
    setLoading(true);
    const response = await apiWrapWithErrorWithData(getAgreementCategories());

    if (Array.isArray(response?.data)) {
      setCategories(response.data);
    } else {
      setCategories([]);
      showNotification({
        color: "red",
        title: "Agreement categories",
        message: response?.message || "Could not load agreement categories.",
      });
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const closeFormModal = () => {
    setModalOpen(false);
    setEditId(null);
    setFormName("");
    setFormIsActive("true");
    setFormError("");
  };

  const openCreateModal = () => {
    setEditId(null);
    setFormName("");
    setFormIsActive("true");
    setFormError("");
    setModalOpen(true);
  };

  const openEditModal = (category) => {
    setEditId(category.id);
    setFormName(category.name || "");
    setFormIsActive(normalizeActive(category.isActive) ? "true" : "false");
    setFormError("");
    setModalOpen(true);
  };

  const handleSave = async () => {
    const trimmedName = formName.trim();
    if (!trimmedName) {
      setFormError("Agreement category name is required.");
      return;
    }

    setSaving(true);
    setFormError("");

    const response = await apiWrapWithErrorWithData(
      editId
        ? updateAgreementCategory({
            id: editId,
            name: trimmedName,
            isActive: formIsActive === "true",
          })
        : createAgreementCategory({ name: trimmedName }),
    );

    if (response?.data) {
      showNotification({
        color: "green",
        title: editId
          ? "Agreement category updated"
          : "Agreement category added",
        message:
          response?.message ||
          (editId
            ? "Agreement category was updated successfully."
            : "Agreement category was added successfully."),
      });
      closeFormModal();
      fetchCategories();
    } else {
      showNotification({
        color: "red",
        title: editId ? "Update agreement category" : "Add agreement category",
        message:
          response?.message ||
          (editId
            ? "Could not update the agreement category."
            : "Could not add the agreement category."),
      });
    }

    setSaving(false);
  };

  const handleDelete = async () => {
    if (!categoryToDelete?.id) return;

    setDeleteLoading(true);
    const response = await apiWrapWithErrorWithData(
      deleteAgreementCategory({ id: categoryToDelete.id }),
    );

    if (
      response?.data ||
      response?.message?.toLowerCase?.().includes("success")
    ) {
      showNotification({
        color: "green",
        title: "Agreement category deleted",
        message:
          response?.message ||
          `"${categoryToDelete.name}" has been deleted successfully.`,
      });
      setDeleteModalOpen(false);
      setCategoryToDelete(null);
      fetchCategories();
    } else {
      showNotification({
        color: "red",
        title: "Delete agreement category",
        message:
          response?.message || "Could not delete the agreement category.",
      });
    }

    setDeleteLoading(false);
  };

  return (
    <>
      <div className="w-full">
        <div className="mb-4 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <Title order={2}>Agreement Categories</Title>
          <Button
            className="rounded-lg bg-[#46BDE1] text-white hover:bg-[#3aa8ca]"
            leftIcon={<IconPlus size={16} />}
            onClick={openCreateModal}
          >
            Add
          </Button>
        </div>

        <Card
          shadow="sm"
          radius="md"
          p={0}
          withBorder
          className="overflow-hidden bg-white"
        >
          {loading ? (
            <div className="flex flex-col items-center justify-center gap-4 py-20">
              <BeatLoader color={colors.primary} size={12} />
              <Text size="sm" color="dimmed">
                Loading agreement categories...
              </Text>
            </div>
          ) : categories.length === 0 ? (
            <div className="flex flex-col items-center justify-center px-6 py-20 text-center">
              <Text fw={500}>No agreement categories found</Text>
              <Text size="sm" color="dimmed" mt={4}>
                {categories.length === 0
                  ? "Add your first agreement category to get started."
                  : "Try adjusting your search or status filter."}
              </Text>
              {categories.length === 0 && (
                <Button
                  className="mt-4 rounded-lg bg-[#46BDE1] text-white hover:bg-[#3aa8ca]"
                  leftIcon={<IconPlus size={16} />}
                  onClick={openCreateModal}
                >
                  Add Agreement Category
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                      ID
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                      Name
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                      Status
                    </th>
                    <th className="px-4 py-3 text-right! text-sm font-semibold text-gray-700">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {categories.map((category, index) => {
                    const isActive = normalizeActive(category.isActive);

                    return (
                      <tr
                        key={category.id}
                        className="border-t border-gray-100"
                      >
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {category.id}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          {category.name}
                        </td>
                        <td className="px-4 py-3">
                          <Badge
                            color={isActive ? "green" : "gray"}
                            variant="light"
                          >
                            {isActive ? "Active" : "Inactive"}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-center gap-2">
                            <ActionIcon
                              variant="light"
                              color="blue"
                              onClick={() => openEditModal(category)}
                              title="Edit agreement category"
                            >
                              <IconEdit size={18} />
                            </ActionIcon>
                            <ActionIcon
                              variant="light"
                              color="red"
                              onClick={() => {
                                setCategoryToDelete(category);
                                setDeleteModalOpen(true);
                              }}
                              title="Delete agreement category"
                            >
                              <IconTrash size={18} />
                            </ActionIcon>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      <Modal
        opened={modalOpen}
        onClose={closeFormModal}
        title={editId ? "Edit Agreement Category" : "Add Agreement Category"}
        centered
      >
        <div className="flex flex-col gap-4">
          <TextInput
            label="Agreement category name"
            placeholder="Enter agreement category name"
            value={formName}
            error={formError}
            onChange={(event) => {
              setFormName(event.currentTarget.value);
              if (formError) setFormError("");
            }}
          />
          {editId && (
            <Select
              label="Status"
              value={formIsActive}
              withinPortal
              data={[
                { value: "true", label: "Active" },
                { value: "false", label: "Inactive" },
              ]}
              onChange={setFormIsActive}
            />
          )}
          <div className="mt-2 flex justify-end gap-3">
            <Button
              variant="default"
              onClick={closeFormModal}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              className="rounded-lg bg-[#46BDE1] text-white hover:bg-[#3aa8ca]"
              loading={saving}
              onClick={handleSave}
            >
              {editId ? "Save Changes" : "Add Category"}
            </Button>
          </div>
        </div>
      </Modal>

      <DeleteConfirmationModal
        open={deleteModalOpen}
        setOpen={setDeleteModalOpen}
        deleteLoading={deleteLoading}
        deleteHandler={handleDelete}
        title="Delete Agreement Category"
        text={`Are you sure you want to delete "${categoryToDelete?.name || "this agreement category"}"? This action cannot be undone.`}
      />
    </>
  );
};

export default AgreementCategory;
